import React, { useEffect, useRef, useState } from "react";
import HekaosFuture from "../data/Hekaos_Future.png";
import PageCTA from '../components/sections/pageCTA.jsx';

// ─── ICON ──────────────────────────────────────────────────────────────────────
const Icon = ({ d, size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"
    strokeLinejoin="round" className={className}>
    <path d={d} />
  </svg>
);

const ICONS = {
  hub: "M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5",
  lab: "M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v11m0 0H5a2 2 0 00-2 2v4a2 2 0 002 2h10a2 2 0 002-2v-4a2 2 0 00-2-2h-4m0 0V3",
  insights: "M13 2L3 14h9l-1 8 10-12h-9l1-8z",
  graph: "M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z",
  whatsapp: "M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z",
  chaos: "M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0 0v-8m0 0L8 9m4 5l4-5",
  heka: "M13 10V3L4 14h7v7l9-11h-7z",
  order: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
  arrow: "M5 12h14M12 5l7 7-7 7",

  cube: "M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z",
  brain: "M12 2a3 3 0 00-3 3v1a3 3 0 00-3 3v3a3 3 0 003 3h6a3 3 0 003-3V9a3 3 0 00-3-3V5a3 3 0 00-3-3z M9 18a3 3 0 003 3 3 3 0 003-3",
  network: "M12 2L2 7l10 5 10-5-10-5z M2 17l10 5 10-5 M2 12l10 5 10-5",
  workflow: "M4 4h4v4H4zm8 0h4v4h-4zm8 0h4v4h-4zM4 12h4v4H4zm8 0h4v4h-4zm8 0h4v4h-4z",
  rocket: "M4.5 16.5c-1.5 1.36-2 3.5-2 5.5l5.5-2s2.14-.5 3.5-2L21 8.5l-6-6L5.5 12c-1.5 1.36-1 3.5-1 4.5z",
  server: "M3 4h18v4H3zm0 6h18v4H3zm0 6h18v4H3z M7 8h.01M7 14h.01M7 20h.01",
  pipeline: "M5 8h14M5 16h14 M3 4h18v16H3z",
  agent: "M12 2L2 7l10 5 10-5-10-5z M2 17l10 5 10-5",
  sparkles: "M12 2l1.09 3.26L16 6.35l-3.26 1.09L12 11l-1.09-3.26L8 6.35l3.26-1.09L12 2z M5 12l.54 1.63L7 14.17l-1.63.54L5 16.34l-.54-1.63L3 14.17l1.63-.54L5 12z M19 12l.54 1.63L21 14.17l-1.63.54L19 16.34l-.54-1.63L17 14.17l1.63-.54L19 12z",

};

// ─── DATA ─────────────────────────────────────────────────────────────────────
const PLATFORMS_DATA = [
  {
    name: "HEKAOS Core Engine",
    tagline: "Intelligence Orchestration Layer",
    desc: "Multi-agent coordination, knowledge graph reasoning, and real-time decision synthesis. The brain behind every platform.",
    color: "#8b5cf6",
    gradient: "from-violet-500 to-purple-600",
    icon: ICONS.brain,
    accent: "cube",
    features: ["Multi-Agent Orchestration", "Knowledge Graph Reasoning", "Real-time Decision Synthesis", "Contextual Memory"],
  },
  {
    name: "AI Pipelines",
    tagline: "End-to-End Model Management",
    desc: "From training to deployment to monitoring—versioned, reproducible, and production-ready AI workflows.",
    color: "#06b6d4",
    gradient: "from-cyan-500 to-blue-600",
    icon: ICONS.pipeline,
    accent: "pipeline",
    features: ["Version Control", "Auto-Scaling Deployment", "Model Monitoring", "A/B Testing Framework"],
  },
  {
    name: "Agent Swarm",
    tagline: "Distributed Intelligence Network",
    desc: "Autonomous agents that collaborate, learn from each other, and solve complex problems through emergent behavior.",
    color: "#ec4899",
    gradient: "from-pink-500 to-rose-600",
    icon: ICONS.agent,
    accent: "swarm",
    features: ["Autonomous Collaboration", "Emergent Problem Solving", "Distributed Learning", "Self-Optimization"],
  },
];

// ─── PLATFORM ACCENTS ──────────────────────────────────────────────────────────
const CubeAccent = () => (
  <div className="relative h-24 flex items-center justify-center perspective-1000">
    <div className="pp-cube-3d w-20 h-20 relative">
      <div className="absolute inset-0 border-2 border-violet-400/40 rounded-lg"
        style={{ transform: "translateZ(-10px)" }} />
      <div className="absolute inset-0 border-2 border-violet-400/60 rounded-lg"
        style={{ transform: "translateZ(10px)" }} />
      <div className="absolute inset-0 bg-violet-400/10 rounded-lg" />
    </div>
  </div>
);

const PipelineAccent = () => (
  <div className="relative w-24 h-20 flex items-center justify-center">
    <svg width="96" height="80" viewBox="0 0 96 80" fill="none">
      {/* Pipeline path */}
      <path d="M 8,40 L 32,40 L 48,20 L 64,20 L 80,40 L 88,40"
        stroke="rgba(6,182,212,0.25)" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      {/* Animated flow */}
      <path d="M 8,40 L 32,40 L 48,20 L 64,20 L 80,40 L 88,40"
        stroke="rgba(6,182,212,0.9)" strokeWidth="3" fill="none"
        strokeDasharray="15 10" className="pp-pipeline-flow" strokeLinecap="round" />
      {/* Pulsing nodes */}
      <circle cx="20" cy="40" r="3.5" fill="rgba(6,182,212,0.6)" className="pp-pipeline-pulse" />
      <circle cx="56" cy="20" r="3.5" fill="rgba(6,182,212,0.6)" className="pp-pipeline-pulse" style={{ animationDelay: '0.5s' }} />
      <circle cx="84" cy="40" r="3.5" fill="rgba(6,182,212,0.6)" className="pp-pipeline-pulse" style={{ animationDelay: '1s' }} />
      {/* Connection nodes */}
      <circle cx="20" cy="40" r="5" fill="none" stroke="rgba(6,182,212,0.4)" strokeWidth="1" className="pp-pipeline-ring" />
      <circle cx="56" cy="20" r="5" fill="none" stroke="rgba(6,182,212,0.4)" strokeWidth="1" className="pp-pipeline-ring" style={{ animationDelay: '0.5s' }} />
      <circle cx="84" cy="40" r="5" fill="none" stroke="rgba(6,182,212,0.4)" strokeWidth="1" className="pp-pipeline-ring" style={{ animationDelay: '1s' }} />
    </svg>
  </div>
);

const SwarmAccent = () => (
  <div className="relative h-24 flex items-center justify-center">
    <div className="w-4 h-4 rounded-full bg-pink-400/60 border border-pink-400" />
    <div className="absolute w-20 h-20 rounded-full border border-dashed border-pink-400/20" />
    {[1, 2, 3].map((i) => (
      <div key={i} className={`absolute pp-swarm-dot-${i}`} style={{ width: 72, height: 72 }}>
        <div className="w-2.5 h-2.5 rounded-full bg-pink-400 shadow-[0_0_10px_rgba(236,72,153,0.8)]" />
      </div>
    ))}
  </div>
);

// ─── STYLES ────────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

  .ic-title {
    background: linear-gradient(110deg, #fbbf24 35%, #a78bfa 50%, #fbbf24 65%);
    background-size: 200% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: ic-shimmer 8s linear infinite;
  }
  @keyframes ic-shimmer {
    0%   { background-position: 0% 50%; }
    100% { background-position: 200% 50%; }
  }

  .ic-dot-grid {
    background-image: radial-gradient(circle, rgba(251,191,36,0.12) 1px, transparent 1px);
    background-size: 28px 28px;
  }

  .ic-divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(251,191,36,0.3), transparent);
  }

  @keyframes ic-fadeUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .ic-fade   { animation: ic-fadeUp 0.7s ease forwards; }
  .ic-fade-1 { animation-delay: 0.05s; opacity: 0; }
  .ic-fade-2 { animation-delay: 0.15s; opacity: 0; }
  .ic-fade-3 { animation-delay: 0.25s; opacity: 0; }
  .ic-fade-4 { animation-delay: 0.35s; opacity: 0; }
  .ic-fade-5 { animation-delay: 0.45s; opacity: 0; }

@keyframes ic-bar-rise {
  from { transform: scaleY(0); opacity: 0; }
  to   { transform: scaleY(1); opacity: 1; }
}
@keyframes ic-radar-sweep {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
@keyframes ic-layer-glow {
  0%, 100% { opacity: 0.6; }
  50%       { opacity: 1; }
}

  /* ── Cycle ── */
  .ic-node { transition: transform 0.3s ease, box-shadow 0.3s ease; }
  .ic-node:hover { transform: translateY(-3px); }

  @keyframes ic-flow {
    0%   { stroke-dashoffset: 24; opacity: 0.3; }
    50%  { opacity: 1; }
    100% { stroke-dashoffset: 0; opacity: 0.3; }
  }
  .ic-arrow-anim       { stroke-dasharray: 8 4; animation: ic-flow 1.8s ease-in-out infinite; }
  .ic-arrow-anim-delay { stroke-dasharray: 8 4; animation: ic-flow 1.8s ease-in-out 0.9s infinite; }

  @keyframes ic-heka-ring {
    0%   { transform: scale(1);    opacity: 0.35; }
    70%  { transform: scale(1.55); opacity: 0; }
    100% { transform: scale(1.55); opacity: 0; }
  }
  .ic-heka-ring { animation: ic-heka-ring 2.4s ease-out infinite; }

  /* ── Bento capabilities ── */
  .ic-bento {
    transition: transform 0.3s cubic-bezier(0.22,1,0.36,1), box-shadow 0.3s ease, border-color 0.3s ease;
  }
  .ic-bento:hover { transform: translateY(-3px); box-shadow: 0 16px 40px rgba(0,0,0,0.3); }

  /* signal line animation for Insights Engine */
  @keyframes ic-signal {
    0%   { stroke-dashoffset: 120; }
    100% { stroke-dashoffset: 0; }
  }
  .ic-signal-line {
    stroke-dasharray: 120;
    animation: ic-signal 2.5s linear infinite;
  }

  /* orbit dot for Knowledge Graphs */
  @keyframes ic-orbit {
    from { transform: rotate(0deg) translateX(28px) rotate(0deg); }
    to   { transform: rotate(360deg) translateX(28px) rotate(-360deg); }
  }
  .ic-orbit-dot { animation: ic-orbit 3s linear infinite; }

  /* waveform pulse for Simulation Lab */
  @keyframes ic-wave {
    0%, 100% { transform: scaleY(1); }
    50%       { transform: scaleY(0.3); }
  }
  .ic-bar-1 { animation: ic-wave 1.1s ease-in-out 0.0s infinite; }
  .ic-bar-2 { animation: ic-wave 1.1s ease-in-out 0.15s infinite; }
  .ic-bar-3 { animation: ic-wave 1.1s ease-in-out 0.30s infinite; }
  .ic-bar-4 { animation: ic-wave 1.1s ease-in-out 0.45s infinite; }
  .ic-bar-5 { animation: ic-wave 1.1s ease-in-out 0.60s infinite; }
  .ic-bar-6 { animation: ic-wave 1.1s ease-in-out 0.75s infinite; }
  .ic-bar-7 { animation: ic-wave 1.1s ease-in-out 0.90s infinite; }

  /* layers stack for Model Hub */
  @keyframes ic-layer-float {
    0%, 100% { transform: translateY(0px); }
    50%       { transform: translateY(-4px); }
  }
  .ic-layer-1 { animation: ic-layer-float 2.8s ease-in-out 0s infinite; }
  .ic-layer-2 { animation: ic-layer-float 2.8s ease-in-out 0.3s infinite; }
  .ic-layer-3 { animation: ic-layer-float 2.8s ease-in-out 0.6s infinite; }

  /* ── Stat arc ── */
  @keyframes ic-arc-draw {
    from { stroke-dashoffset: var(--arc-len); }
    to   { stroke-dashoffset: 0; }
  }
  .ic-arc { animation: ic-arc-draw 1.4s cubic-bezier(0.22,1,0.36,1) forwards; }

  /* count-up number */
  @keyframes ic-count-pop {
    0%   { opacity: 0; transform: scale(0.8) translateY(8px); }
    100% { opacity: 1; transform: scale(1) translateY(0); }
  }
  .ic-count { animation: ic-count-pop 0.6s cubic-bezier(0.22,1,0.36,1) 0.3s both; }

  /* ── Horizontal timeline ── */
  @keyframes ic-track-draw {
    from { width: 0%; }
    to   { width: 100%; }
  }
  .ic-track-fill { animation: ic-track-draw 1.6s cubic-bezier(0.22,1,0.36,1) 0.4s both; }

  .ic-milestone {
    transition: transform 0.25s cubic-bezier(0.22,1,0.36,1);
    cursor: default;
  }
  .ic-milestone:hover { transform: translateY(-4px); }
  .ic-milestone:hover .ic-ms-card { border-color: rgba(167,139,250,0.4); background: rgba(30,27,75,0.5); }

  .ic-ms-card {
    transition: border-color 0.25s ease, background 0.25s ease;
  }

  /* ── Buttons ── */
  .ic-btn-primary {
    position: relative; overflow: hidden;
    transition: all 0.3s ease;
  }
  .ic-btn-primary::after {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent);
    transform: translateX(-100%);
    transition: transform 0.5s ease;
  }
  .ic-btn-primary:hover::after { transform: translateX(100%); }
  .ic-btn-primary:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(217,119,6,0.35);
    background-color: #d97706;
  }
  .ic-btn-secondary { transition: all 0.3s ease; }
  .ic-btn-secondary:hover {
    transform: translateY(-2px);
    background: rgba(109,40,217,0.1);
    border-color: rgba(109,40,217,0.6);
    color: #c4b5fd;
  }
`;

// ─── DATA ─────────────────────────────────────────────────────────────────────
const STATS = [
  { value: 47, suffix: "", label: "Intelligence\nNodes Active", color: "#fbbf24", pct: 0.78 },
  { value: 12, suffix: "+", label: "AI Models\nDeployed", color: "#a78bfa", pct: 0.60 },
  { value: 99.9, suffix: "%", label: "System\nUptime", color: "#22d3ee", pct: 0.999 },
  { value: 1.2, suffix: "M", label: "Scenarios\nSimulated", color: "#34d399", pct: 0.85 },
];

const MILESTONES = [
  {
    phase: "2025",
    title: "Blueprint",
    desc: "Research hub takes shape. Models, methods, and decision systems built on evidence.",
    color: "#fbbf24",
    done: true,
  },
  {
    phase: "2026",
    title: "Lab",
    desc: "Experiments become artifacts. Prototypes become testable systems.",
    color: "#a78bfa",
    done: true,
  },
  {
    phase: "2027",
    title: "Agents",
    desc: "Multi-agent simulations and real-time reasoning loops, built for reliability.",
    color: "#22d3ee",
    done: true,
  },
  {
    phase: "Next",
    title: "Foresight Engine",
    desc: "A living decision surface — connecting data, AI, and intent.",
    color: "#34d399",
    done: false,
    pulse: true,
  },
];

// ─── COUNT-UP HOOK ────────────────────────────────────────────────────────────
const useCountUp = (target, duration = 1400, start = false) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    const steps = 60;
    const inc = target / steps;
    let cur = 0;
    const t = setInterval(() => {
      cur = Math.min(cur + inc, target);
      setVal(cur);
      if (cur >= target) clearInterval(t);
    }, duration / steps);
    return () => clearInterval(t);
  }, [target, duration, start]);
  return val;
};

// ─── STAT CARD ────────────────────────────────────────────────────────────────
const StatCard = ({ stat, active }) => {
  const R = 52, C = 64, stroke = 6;
  const circumference = 2 * Math.PI * R;
  const arcLen = circumference * stat.pct;
  const dashOffset = circumference - arcLen;
  const count = useCountUp(stat.value, 1400, active);

  const display = stat.suffix === "M"
    ? count.toFixed(1) + "M"
    : stat.suffix === "%"
      ? count.toFixed(1) + "%"
      : Math.round(count) + stat.suffix;

  return (
    <div className="flex flex-col items-center gap-3 py-8 px-4">
      {/* Arc SVG */}
      <div className="relative" style={{ width: C * 2, height: C * 2 }}>
        <svg width={C * 2} height={C * 2} style={{ transform: "rotate(-90deg)" }}>
          {/* Track */}
          <circle cx={C} cy={C} r={R} fill="none"
            stroke="rgba(255,255,255,0.05)" strokeWidth={stroke} />
          {/* Fill arc */}
          {active && (
            <circle cx={C} cy={C} r={R} fill="none"
              stroke={stat.color} strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              className="ic-arc"
              style={{ "--arc-len": circumference }}
            />
          )}
        </svg>
        {/* Center number */}
        <div className="absolute inset-0 flex items-center justify-center ic-count">
          <span className="text-2xl font-bold tabular-nums" style={{ color: stat.color }}>
            {active ? display : "—"}
          </span>
        </div>
      </div>
      <p className="text-xs text-slate-500 text-center whitespace-pre-line leading-relaxed">
        {stat.label}
      </p>
    </div>
  );
};

// ─── STATS SECTION ────────────────────────────────────────────────────────────
// const StatsSection = () => {
//   const ref = useRef(null);
//   const [active, setActive] = useState(false);
//   useEffect(() => {
//     const obs = new IntersectionObserver(
//       ([e]) => { if (e.isIntersecting) { setActive(true); obs.disconnect(); } },
//       { threshold: 0.3 }
//     );
//     if (ref.current) obs.observe(ref.current);
//     return () => obs.disconnect();
//   }, []);

//   return (
//     <section ref={ref} className="ic-fade ic-fade-3">
//       <div className="text-center mb-8">
//         <p className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">
//           By The Numbers
//         </p>
//         <h2 className="text-2xl font-semibold text-slate-100 tracking-tight">
//           HEKAOS in Operation
//         </h2>
//       </div>

//       <div className="rounded-2xl border border-slate-700/50 overflow-hidden"
//         style={{ background: "rgba(15,23,42,0.5)" }}>
//         {/* top accent */}
//         <div className="h-px bg-gradient-to-r from-transparent via-amber-500/40 to-transparent" />

//         <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-slate-700/40">
//           {STATS.map((s) => (
//             <StatCard key={s.label} stat={s} active={active} />
//           ))}
//         </div>
//       </div>
//     </section>
//   );
// };

// ─── BENTO CAPABILITIES ───────────────────────────────────────────────────────

{/* ── NOESIS PLATFORM ACCENTS ─────────────────────────────────────── */}

{/* SDK Accent — cascading code lines with pulsing endpoint */}
const SDKAccent = () => (
  <div className="relative h-20 flex items-end overflow-hidden">
    <svg width="100%" height="80" viewBox="0 0 200 80" fill="none" preserveAspectRatio="none">
      {[0,1,2,3,4].map((i) => (
        <line key={i}
          x1="0" y1={12 + i * 14} x2="160" y2={12 + i * 14}
          stroke={`rgba(251,191,36,${0.06 + i * 0.05})`}
          strokeWidth="1"
          strokeDasharray="4 3"
          style={{ animation: `ic-flow ${1.4 + i * 0.2}s ease-in-out ${i * 0.15}s infinite` }}
        />
      ))}
      {/* Active line */}
      <line x1="0" y1="54" x2="140" y2="54"
        stroke="rgba(251,191,36,0.6)" strokeWidth="1.5"
        strokeDasharray="4 3"
        style={{ animation: "ic-flow 1.2s ease-in-out infinite" }}
      />
      {/* Endpoint pulse */}
      <circle cx="140" cy="54" r="4" fill="rgba(251,191,36,0.9)" className="ic-node" style={{ animationDelay: "0s" }} />
      <circle cx="140" cy="54" r="8" fill="none" stroke="rgba(251,191,36,0.3)" strokeWidth="1" />
    </svg>
  </div>
);

{/* Studio Accent — animated bar chart building upward */}
const StudioAccent = () => (
  <div className="flex items-end gap-1.5 h-14">
    {[0.4, 0.7, 0.55, 1, 0.65, 0.85, 0.5, 0.9].map((h, i) => (
      <div key={i}
        className="rounded-sm"
        style={{
          width: 7,
          height: 52 * h,
          background: `linear-gradient(to top, rgba(167,139,250,${0.5 + h * 0.4}), rgba(167,139,250,${0.15 + h * 0.15}))`,
          transformOrigin: "bottom",
          animation: `ic-bar-rise 1.6s ease-out ${i * 0.08}s both`,
        }}
      />
    ))}
  </div>
);

{/* Intel Accent — radar sweep */}
const IntelAccent = () => (
  <div className="relative h-14 flex items-center justify-center overflow-hidden">
    <svg width="80" height="56" viewBox="0 0 80 56" fill="none">
      {/* Rings */}
      <circle cx="40" cy="28" r="24" stroke="rgba(6,182,212,0.15)" strokeWidth="1" strokeDasharray="3 3" />
      <circle cx="40" cy="28" r="16" stroke="rgba(6,182,212,0.25)" strokeWidth="1" strokeDasharray="3 3" />
      <circle cx="40" cy="28" r="8"  stroke="rgba(6,182,212,0.4)"  strokeWidth="1" />
      {/* Sweep line */}
      <line x1="40" y1="28" x2="40" y2="4"
        stroke="rgba(6,182,212,0.8)" strokeWidth="1.5" strokeLinecap="round"
        style={{ transformOrigin: "40px 28px", animation: "ic-radar-sweep 2.4s linear infinite" }}
      />
      {/* Center dot */}
      <circle cx="40" cy="28" r="2.5" fill="rgba(6,182,212,1)" />
      {/* Signal blip */}
      <circle cx="56" cy="14" r="2" fill="rgba(6,182,212,0.7)" className="ic-node" style={{ animationDelay: "0.6s" }} />
    </svg>
  </div>
);

{/* Core Accent — stacked layers illuminating bottom to top */}
const CoreAccent = () => (
  <div className="relative h-20 flex items-center justify-center">
    <svg width="130" height="80" viewBox="0 0 130 80" fill="none">
      {[0,1,2,3].map((i) => (
        <g key={i} style={{ animation: `ic-layer-glow 2.4s ease-in-out ${(3 - i) * 0.3}s infinite` }}>
          <rect
            x={10 + i * 8} y={58 - i * 14}
            width={110 - i * 16} height={10}
            rx="3"
            fill={`rgba(52,211,153,${0.08 + i * 0.06})`}
            stroke={`rgba(52,211,153,${0.2 + i * 0.15})`}
            strokeWidth="1"
          />
        </g>
      ))}
      {/* Top active layer — brightest */}
      <rect x="34" y="2" width="62" height="10" rx="3"
        fill="rgba(52,211,153,0.25)"
        stroke="rgba(52,211,153,0.7)"
        strokeWidth="1.5"
      />
      <circle cx="65" cy="7" r="2.5" fill="rgba(52,211,153,0.9)" className="ic-node" style={{ animationDelay: "0s" }} />
    </svg>
  </div>
);


// Model Hub accent — animated stacked layers
const LayerAccent = () => (
  <div className="relative h-16 flex items-end justify-center gap-1">
    {[1, 2, 3].map((i) => (
      <div key={i}
        className={`rounded-sm ic-layer-${i}`}
        style={{
          width: 48 + i * 16,
          height: 10,
          background: `rgba(251,191,36,${0.1 + i * 0.12})`,
          border: "1px solid rgba(251,191,36,0.2)",
          position: "absolute",
          bottom: (i - 1) * 13,
        }}
      />
    ))}
  </div>
);

// Simulation Lab accent — animated waveform bars
const WaveAccent = () => (
  <div className="flex items-center gap-1 h-12">
    {[0.4, 0.7, 1, 0.6, 0.9, 0.5, 0.8].map((h, i) => (
      <div key={i}
        className={`rounded-full ic-bar-${i + 1}`}
        style={{
          width: 4,
          height: 40 * h,
          background: `rgba(167,139,250,${0.3 + h * 0.4})`,
          transformOrigin: "bottom",
        }}
      />
    ))}
  </div>
);

// Insights Engine accent — animated signal line SVG
const SignalAccent = () => (
  <svg width="100%" height="40" viewBox="0 0 200 40" fill="none" preserveAspectRatio="none">
    <polyline
      points="0,30 20,30 30,10 45,35 60,15 75,30 95,30 110,8 125,32 140,20 160,30 200,30"
      stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      fill="none" className="ic-signal-line"
      style={{ strokeDasharray: 300, animation: "ic-signal 2.5s linear infinite" }}
    />
    <polyline
      points="0,30 20,30 30,10 45,35 60,15 75,30 95,30 110,8 125,32 140,20 160,30 200,30"
      stroke="rgba(251,191,36,0.12)" strokeWidth="2" fill="none"
    />
  </svg>
);

// Knowledge Graphs accent — orbiting dots
const OrbitAccent = () => (
  <div className="relative flex items-center justify-center" style={{ width: 80, height: 80 }}>
    {/* center node */}
    <div className="w-4 h-4 rounded-full bg-green-400/40 border border-green-400/60" />
    {/* orbit ring */}
    <div className="absolute w-16 h-16 rounded-full border border-dashed border-green-400/20" />
    {/* orbiting dot */}
    <div className="absolute" style={{ width: 56, height: 56 }}>
      <div className="absolute top-1/2 left-1/2 ic-orbit-dot"
        style={{ width: 8, height: 8, marginTop: -4, marginLeft: -4 }}>
        <div className="w-2 h-2 rounded-full bg-green-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]" />
      </div>
    </div>
    {/* second orbit, opposite phase */}
    <div className="absolute w-12 h-12 rounded-full border border-dashed border-green-400/10" />
    <div className="absolute" style={{ width: 44, height: 44 }}>
      <div className="absolute top-1/2 left-1/2"
        style={{
          width: 6, height: 6, marginTop: -3, marginLeft: -3,
          animation: "ic-orbit 2s linear 1.5s infinite",
          transform: "rotate(180deg) translateX(22px) rotate(-180deg)",
        }}>
        <div className="w-1.5 h-1.5 rounded-full bg-green-400/60" />
      </div>
    </div>
  </div>
);

const CapabilitiesSection = () => (
  <section className="ic-fade ic-fade-3">
    <div className="text-center mb-10">
      <p className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">
        Core Systems
      </p>
      <h2 className="text-2xl font-semibold text-slate-100 tracking-tight">
        Intelligence Capabilities
      </h2>
    </div>


 {/* ── BENTO GRID ───────────────────────────────────────────────────── */}
<div className="grid grid-cols-1 md:grid-cols-3 md:grid-rows-2 gap-4 md:h-[480px]">

{/* NOESIS SDK — large, spans 2 rows */}
<div className="ic-bento md:row-span-2 rounded-2xl border border-amber-800/30 hover:border-amber-500/40 bg-slate-900 p-7 flex flex-col justify-between overflow-hidden relative">
  <div className="absolute inset-0 pointer-events-none"
    style={{ background: "radial-gradient(ellipse at 20% 80%, rgba(251,191,36,0.06) 0%, transparent 60%)" }} />
  <div>
    <div className="w-12 h-12 rounded-xl bg-amber-900/30 border border-amber-800/40 flex items-center justify-center mb-5">
      <Icon d={ICONS.hub} size={22} className="text-amber-400" />
    </div>
    <h3 className="text-amber-400 font-bold text-lg tracking-tight mb-1">NOESIS SDK</h3>
    <p className="text-amber-300/60 text-xs font-semibold tracking-wide mb-3">Intelligence you can install.</p>
    <p className="text-slate-400 text-sm leading-relaxed">
      Developer-first toolkit for embedding HEKAOS intelligence into any application. APIs, modules, and agent frameworks built for teams who want to build on top of structured intelligence — not rebuild it from scratch.
    </p>
  </div>
  <div className="mt-6 relative h-20 flex items-end">
    <SDKAccent />
    <p className="absolute bottom-0 right-0 text-xs text-amber-400/40 font-semibold tracking-widest uppercase">
      v1.7 · Closed Access
    </p>
  </div>
</div>

{/* NOESIS Studio */}
<div className="ic-bento rounded-2xl border border-violet-800/30 hover:border-violet-500/40 bg-slate-900 p-6 flex flex-col justify-between overflow-hidden relative">
  <div className="absolute inset-0 pointer-events-none"
    style={{ background: "radial-gradient(ellipse at 80% 20%, rgba(167,139,250,0.06) 0%, transparent 60%)" }} />
  <div>
    <div className="flex items-center justify-between mb-4">
      <div className="w-11 h-11 rounded-xl bg-violet-900/30 border border-violet-800/40 flex items-center justify-center">
        <Icon d={ICONS.lab} size={20} className="text-violet-400" />
      </div>
      <WaveAccent />
    </div>
    <h3 className="text-violet-400 font-bold text-base tracking-tight mb-1">NOESIS Studio</h3>
    <p className="text-violet-300/60 text-xs font-semibold tracking-wide mb-2">Intelligence, made visible.</p>
    <p className="text-slate-400 text-xs leading-relaxed">
      Visual intelligence components — dashboards, infographics, and data views built for enterprise teams and individuals who need insight without the engineering overhead.
    </p>
  </div>
</div>

{/* NOESIS Intel */}
<div className="ic-bento rounded-2xl border border-cyan-800/30 hover:border-cyan-500/40 bg-slate-900 p-6 flex flex-col justify-between overflow-hidden relative">
  <div className="absolute inset-0 pointer-events-none"
    style={{ background: "radial-gradient(ellipse at 50% 100%, rgba(6,182,212,0.05) 0%, transparent 60%)" }} />
  <div>
    <div className="flex items-center justify-between mb-4">
      <div className="w-11 h-11 rounded-xl bg-cyan-900/30 border border-cyan-800/40 flex items-center justify-center">
        <Icon d={ICONS.insights} size={20} className="text-cyan-400" />
      </div>
      <IntelAccent />
    </div>
    <h3 className="text-cyan-400 font-bold text-base tracking-tight mb-1">NOESIS Intel</h3>
    <p className="text-cyan-300/60 text-xs font-semibold tracking-wide mb-2">Signal over noise.</p>
    <p className="text-slate-400 text-xs leading-relaxed">
      A living feed of research, pattern reports, and decision intelligence — continuously updated, always traceable to source.
    </p>
  </div>
</div>

{/* NOESIS Core — wide, spans 2 columns */}
<div className="ic-bento md:col-span-2 rounded-2xl border border-emerald-800/25 hover:border-emerald-500/35 bg-slate-900 p-6 flex items-center gap-8 overflow-hidden relative">
  <div className="absolute inset-0 pointer-events-none"
    style={{ background: "radial-gradient(ellipse at 100% 50%, rgba(52,211,153,0.05) 0%, transparent 60%)" }} />
  <CoreAccent />
  <div className="flex-1 min-w-0">
    <div className="w-11 h-11 rounded-xl bg-emerald-900/25 border border-emerald-800/35 flex items-center justify-center mb-4">
      <Icon d={ICONS.graph} size={20} className="text-emerald-400" />
    </div>
    <h3 className="text-emerald-400 font-bold text-base tracking-tight mb-1">NOESIS Core</h3>
    <p className="text-emerald-300/60 text-xs font-semibold tracking-wide mb-2">The foundation everything runs on.</p>
    <p className="text-slate-400 text-xs leading-relaxed">
      Pre-built enterprise building blocks — workflows, pipelines, and agent frameworks production-ready from day one.
    </p>
  </div>
</div>

</div>


  </section>
);

// ─── TIMELINE SECTION ─────────────────────────────────────────────────────────
const TimelineSection = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setActive(true); obs.disconnect(); } },
      { threshold: 0.2 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="ic-fade ic-fade-4">
      <div className="text-center mb-12">
        <p className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">
          Research Journey
        </p>
        <h2 className="text-2xl font-semibold text-slate-100 tracking-tight">
          How We Got Here
        </h2>
      </div>

      {/* ── Desktop horizontal track ── */}
      <div className="hidden md:block relative px-8">
        {/* Background track */}
        <div className="absolute left-8 right-8 top-[3.25rem] h-px bg-slate-800" />
        {/* Animated fill */}
        {active && (
          <div className="absolute left-8 top-[3.25rem] h-px ic-track-fill"
            style={{ background: "linear-gradient(90deg, #fbbf24, #a78bfa, #22d3ee, #34d399)" }} />
        )}

        {/* Milestones */}
        <div className="grid grid-cols-4 gap-4">
          {MILESTONES.map((m, i) => (
            <div key={m.phase} className="ic-milestone flex flex-col items-center">
              {/* Phase label above dot */}
              <p className="text-xs font-bold tracking-widest uppercase mb-3"
                style={{ color: m.color }}>{m.phase}</p>

              {/* Dot on track */}
              <div className="relative flex items-center justify-center w-6 h-6 mb-4">
                {m.pulse && (
                  <span className="absolute w-6 h-6 rounded-full animate-ping"
                    style={{ background: m.color, opacity: 0.25 }} />
                )}
                <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-950 relative z-10"
                  style={{ background: m.color, boxShadow: `0 0 10px ${m.color}60` }} />
              </div>

              {/* Card below */}
              <div className="ic-ms-card w-full rounded-xl border border-slate-700/40 bg-slate-900/60 p-4 text-center">
                <p className="text-slate-100 font-semibold text-sm mb-1.5">{m.title}</p>
                <p className="text-slate-500 text-xs leading-relaxed">{m.desc}</p>
                {!m.done && (
                  <span className="inline-block mt-2 px-2 py-0.5 rounded-full text-xs font-semibold"
                    style={{ background: `${m.color}15`, color: m.color, border: `1px solid ${m.color}30` }}>
                    Upcoming
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Mobile vertical ── */}
      <div className="md:hidden relative max-w-sm mx-auto">
        <div className="absolute left-5 top-2 bottom-2 w-px"
          style={{ background: "linear-gradient(to bottom, #fbbf24, #a78bfa, #22d3ee, #34d399 80%, transparent)" }} />
        <div className="space-y-4">
          {MILESTONES.map((m) => (
            <div key={m.phase} className="relative pl-14">
              <div className="absolute left-5 top-4 -translate-x-1/2 relative z-10">
                {m.pulse && (
                  <span className="absolute w-5 h-5 rounded-full animate-ping -top-1 -left-1"
                    style={{ background: m.color, opacity: 0.25 }} />
                )}
                <div className="w-3 h-3 rounded-full"
                  style={{ background: m.color, boxShadow: `0 0 0 2.5px #020617, 0 0 0 4px ${m.color}30` }} />
              </div>
              <div className="rounded-xl border border-slate-700/40 bg-slate-900/60 p-4">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-xs font-bold uppercase tracking-widest" style={{ color: m.color }}>{m.phase}</span>
                  <span className="text-slate-600 text-xs">—</span>
                  <span className="text-sm font-semibold text-slate-100">{m.title}</span>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">{m.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── PAGE ─────────────────────────────────────────────────────────────────────
const IntelligencePage = ({ setCurrentPage = () => { } }) => (
  <>
    <style>{styles}</style>
    <div className="space-y-20 max-w-6xl mx-auto px-4 pb-20">

      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="relative pt-16 pb-10 text-center ic-fade ic-fade-1">
        <div className="ic-dot-grid absolute inset-0 rounded-2xl -z-10 opacity-60" />
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-amber-600/30 bg-amber-900/10 mb-6">
          <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
          <span className="text-amber-400 text-xs font-semibold tracking-widest uppercase">Intelligence Corner</span>
        </div>
        <h1 className="text-5xl md:text-6xl font-extrabold leading-tight mb-5">
          <span className="ic-title">Hekaos Noesis</span>
        </h1>
        <p className="text-slate-400 max-w-xl mx-auto text-base leading-relaxed">
          Where minds, systems, and intent converge — insight, foresight, and reasoning
          that survives uncertainty and shapes decisive action.
        </p>
        <div className="flex justify-center mt-8">
          <img src={HekaosFuture} alt="Hekaos Intelligence" className="w-1/2 h-auto opacity-80" />
        </div>
      </section>

      <div className="ic-divider" />

      {/* ── CYCLE ────────────────────────────────────────── */}
      <section className="ic-fade ic-fade-2">
        <div className="text-center mb-10">
          <p className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Intelligence cycle</p>
          <h2 className="text-2xl font-semibold text-slate-100 tracking-tight">How NOESIS Processes Intelligence</h2>
        </div>

        {/* Desktop */}
        <div className="hidden md:flex items-center justify-center gap-0 max-w-5xl mx-auto">
          <div className="ic-node flex flex-col items-center gap-4 w-56">
            <div className="w-24 h-24 rounded-2xl border border-red-800/40 bg-red-900/20 flex items-center justify-center"
              style={{ boxShadow: "0 0 28px rgba(239,68,68,0.12)" }}>
              <Icon d={ICONS.chaos} size={38} className="text-red-400" />
            </div>
            <div className="text-center">
              <p className="text-red-400 font-bold text-sm tracking-widest uppercase mb-1.5">SIGNAL</p>
              <p className="text-slate-500 text-sm leading-relaxed">Raw data.<br />Unprocessed intelligence.</p>
            </div>
          </div>

          <div className="flex flex-col items-center gap-2 w-28 flex-shrink-0 pb-14">
            <svg width="112" height="24" viewBox="0 0 112 24" fill="none">
              <line x1="0" y1="12" x2="96" y2="12" stroke="#ef4444" strokeWidth="2" className="ic-arrow-anim" />
              <polyline points="86,5 100,12 86,19" stroke="#ef4444" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="text-slate-600 text-xs font-semibold tracking-widest uppercase">INGEST</span>
          </div>

          <div className="ic-node flex flex-col items-center gap-4 relative">
            <div className="absolute w-36 h-36 rounded-full border border-violet-500/15 ic-heka-ring" />
            <div className="w-36 h-36 rounded-2xl border border-violet-600/50 flex items-center justify-center relative z-10"
              style={{
                background: "linear-gradient(135deg, rgba(109,40,217,0.28), rgba(139,92,246,0.16))",
                boxShadow: "0 0 48px rgba(139,92,246,0.22), inset 0 1px 0 rgba(167,139,250,0.15)",
              }}>
              <Icon d={ICONS.heka} size={52} className="text-violet-400" />
            </div>
            <div className="text-center mt-1">
              <p className="text-violet-400 font-bold text-base tracking-widest uppercase mb-1.5">NOESIS</p>
              <p className="text-slate-500 text-sm leading-relaxed max-w-[11rem]">Intelligence engine.<br />Pattern from noise.</p>
            </div>
          </div>

          <div className="flex flex-col items-center gap-2 w-28 flex-shrink-0 pb-14">
            <svg width="112" height="24" viewBox="0 0 112 24" fill="none">
              <line x1="0" y1="12" x2="96" y2="12" stroke="#22d3ee" strokeWidth="2" className="ic-arrow-anim-delay" />
              <polyline points="86,5 100,12 86,19" stroke="#22d3ee" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="text-slate-600 text-xs font-semibold tracking-widest uppercase">SYNTHESIZE</span>
          </div>

          <div className="ic-node flex flex-col items-center gap-4 w-56">
            <div className="w-24 h-24 rounded-2xl border border-cyan-800/40 bg-cyan-900/20 flex items-center justify-center"
              style={{ boxShadow: "0 0 28px rgba(34,211,238,0.12)" }}>
              <Icon d={ICONS.order} size={38} className="text-cyan-400" />
            </div>
            <div className="text-center">
              <p className="text-cyan-400 font-bold text-sm tracking-widest uppercase mb-1.5">INSIGHT</p>
              <p className="text-slate-500 text-sm leading-relaxed">Structured intelligence.<br />Ready for decisions.</p>
            </div>
          </div>
        </div>

        {/* Mobile */}
        <div className="md:hidden flex flex-col items-center gap-0 max-w-sm mx-auto">
          <div className="ic-node flex items-center gap-5 w-full rounded-xl border border-red-800/30 bg-slate-900 px-6 py-5">
            <div className="w-16 h-16 rounded-xl border border-red-800/40 bg-red-900/20 flex items-center justify-center flex-shrink-0">
              <Icon d={ICONS.chaos} size={28} className="text-red-400" />
            </div>
            <div>
            <p className="text-red-400 font-bold text-sm tracking-widest uppercase mb-1">SIGNAL</p>
<p className="text-slate-500 text-sm leading-relaxed">Raw data. Unprocessed intelligence.</p>

            </div>
          </div>
          <div className="flex flex-col items-center py-3">
            <svg width="24" height="44" viewBox="0 0 24 44" fill="none">
              <line x1="12" y1="0" x2="12" y2="34" stroke="#a78bfa" strokeWidth="2" strokeDasharray="6 3"
                style={{ animation: "ic-flow 1.8s ease-in-out infinite" }} />
              <polyline points="5,26 12,38 19,26" stroke="#a78bfa" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="ic-node flex items-center gap-5 w-full rounded-xl px-6 py-5"
            style={{ border: "1px solid rgba(139,92,246,0.4)", background: "linear-gradient(135deg, rgba(109,40,217,0.2), rgba(139,92,246,0.1))", boxShadow: "0 0 32px rgba(139,92,246,0.18)" }}>
            <div className="w-16 h-16 rounded-xl border border-violet-600/40 bg-violet-900/30 flex items-center justify-center flex-shrink-0">
              <Icon d={ICONS.heka} size={30} className="text-violet-400" />
            </div>
            <div>
              <p className="text-violet-400 font-bold text-sm tracking-widest uppercase mb-1">NOESIS</p>
              <p className="text-slate-400 text-sm leading-relaxed">Intelligence engine. Pattern from noise.</p>
            </div>
          </div>
          <div className="flex flex-col items-center py-3">
            <svg width="24" height="44" viewBox="0 0 24 44" fill="none">
              <line x1="12" y1="0" x2="12" y2="34" stroke="#22d3ee" strokeWidth="2" strokeDasharray="6 3"
                style={{ animation: "ic-flow 1.8s ease-in-out 0.9s infinite" }} />
              <polyline points="5,26 12,38 19,26" stroke="#22d3ee" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="ic-node flex items-center gap-5 w-full rounded-xl border border-cyan-800/30 bg-slate-900 px-6 py-5">
            <div className="w-16 h-16 rounded-xl border border-cyan-800/40 bg-cyan-900/20 flex items-center justify-center flex-shrink-0">
              <Icon d={ICONS.order} size={28} className="text-cyan-400" />
            </div>
            <div>
              <p className="text-cyan-400 font-bold text-sm tracking-widest uppercase mb-1">INSIGHT</p>
              <p className="text-slate-500 text-sm leading-relaxed">Structured intelligence. Ready for decisions.</p>
            </div>
          </div>
        </div>
      </section>

      <div className="ic-divider" />

      {/* ── CAPABILITIES BENTO ───────────────────────────── */}
      <CapabilitiesSection />

      {/* <div className="ic-divider" /> */}

      {/* ── STATS ARCS ───────────────────────────────────── */}
      {/* <StatsSection /> */}

      <div className="ic-divider" />

      {/* ── TIMELINE TRACK ───────────────────────────────── */}
      {/* <TimelineSection /> */}

      {/* <div className="ic-divider" /> */}

      {/* ── CTA ──────────────────────────────────────────── */}
      <PageCTA page="IntelligenceCorner" setCurrentPage={setCurrentPage} />

    </div>
  </>
);

export default IntelligencePage;